/**
 * Cookie consent, and the Google Consent Mode v2 signals it maps onto.
 *
 * **This file exists once per surface, byte for byte**, and is meant to:
 *
 *     shared/spa/src/utils/consent.ts            the dashboard, app.<brand>
 *     proxyforge.io/website/src/lib/consent.ts   the marketing site, proxyforge.io
 *     proxyhive.io/website/lib/consent.ts        the marketing site, proxyhive.io
 *     proxymonkey.io/website/lib/consent.ts      the marketing site, proxymonkey.io
 *     proxypanda.io/website/src/lib/consent.ts   the marketing site, proxypanda.io
 *
 * They are separate builds in separate repositories serving hostnames under one
 * registrable domain, and they read and write **one cookie**. If they disagree
 * about its name, its JSON shape, which Consent Mode signals a category owns,
 * or which regions must be asked at all, a visitor who accepts on the marketing
 * site is asked again on the dashboard — or worse, is counted as consenting to
 * something they declined.
 *
 * A shared package would be the usual answer and is not available: separate
 * repositories, separate lockfiles, one npm and one pnpm, nothing published to
 * a registry. What makes a copy safe instead is that this module is pure and
 * imports nothing, so the whole agreement is one command:
 *
 *     make check-consent-copies
 *
 * Empty output is the contract, and **the list above is part of it** — a surface
 * that copies this file without adding its line here is a copy nobody knows to
 * diff. Something runs that comparison now; when there were two copies nothing
 * did, which is what #479 is open about. Keep it importable from anywhere: the
 * moment this file reaches for React, an alias or a framework, that diff stops
 * being the whole answer.
 */
/**
 * What a visitor is asked about.
 *
 * Strictly-necessary storage is not a category: it carries the session, the
 * consent record itself and CSRF defence, has no opt-out, and offering a
 * toggle that cannot be turned off is a dark pattern rather than a choice.
 */
export const CONSENT_CATEGORIES = ['analytics', 'marketing', 'preferences'] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

export type ConsentChoices = Record<ConsentCategory, boolean>;

export interface ConsentRecord {
  /** Bumped when the categories or their meaning change; see {@link CONSENT_VERSION}. */
  v: number;
  /** ISO 8601. The GDPR asks us to be able to say *when* consent was given. */
  ts: string;
  categories: ConsentChoices;
}

/**
 * Cookie name, shared with the dashboard SPA.
 *
 * Brand-neutral on purpose: the SPA serves four brands from one bundle, so a
 * `proxyhive_`-prefixed name would need four spellings there and the shared
 * logic would stop being shared.
 */
export const CONSENT_COOKIE = 'cookie_consent';

/**
 * Raise this to re-ask everybody.
 *
 * A stored record from an older version is treated as absent rather than
 * upgraded: if the question changed, the old answer is not an answer to it.
 */
export const CONSENT_VERSION = 1;

/** Six months. Long enough not to nag, short enough to be a current answer. */
export const CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

/** Nothing optional, until asked. This is also what Consent Mode defaults to. */
export const CONSENT_DENIED: ConsentChoices = {
  analytics: false,
  marketing: false,
  preferences: false,
};

export const CONSENT_GRANTED: ConsentChoices = {
  analytics: true,
  marketing: true,
  preferences: true,
};

/** Fired on `window` when something asks for the preferences panel. */
export const OPEN_CONSENT_SETTINGS_EVENT = 'consent:open-settings';

type ConsentSignal = 'granted' | 'denied';

/**
 * Every Consent Mode v2 signal Google reads, and which category owns it.
 *
 * `security_storage` has no entry because it is never denied — it covers
 * authentication and fraud prevention, which is the strictly-necessary bucket.
 */
export interface ConsentModePayload {
  ad_storage: ConsentSignal;
  ad_user_data: ConsentSignal;
  ad_personalization: ConsentSignal;
  analytics_storage: ConsentSignal;
  functionality_storage: ConsentSignal;
  personalization_storage: ConsentSignal;
  security_storage: 'granted';
}

const signal = (granted: boolean): ConsentSignal => (granted ? 'granted' : 'denied');

/**
 * Which Consent Mode signals each category owns.
 *
 * **This table is the single definition of the mapping**, and it exists as
 * data rather than as a `return { ... }` because it has two consumers that
 * cannot share code: {@link consentModePayload} below, and the inline
 * `consent default` script the page must run before gtm.js loads, which is
 * serialised JavaScript and so gets this table as JSON. Writing the mapping
 * out twice is how the banner and the default end up disagreeing.
 *
 * The v2 additions — `ad_user_data` and `ad_personalization` — are what the
 * EEA has required for anything ads-related since March 2024. Omitting them
 * does not default them to something sensible; it makes Google treat the page
 * as unconsented outright.
 */
export const CATEGORY_SIGNALS: Record<
  ConsentCategory,
  readonly Exclude<keyof ConsentModePayload, 'security_storage'>[]
> = {
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  preferences: ['functionality_storage', 'personalization_storage'],
};

/** Translate our three categories into the seven signals GTM understands. */
export function consentModePayload(choices: ConsentChoices): ConsentModePayload {
  const payload = { security_storage: 'granted' } as ConsentModePayload;

  for (const category of CONSENT_CATEGORIES) {
    for (const name of CATEGORY_SIGNALS[category]) {
      payload[name] = signal(choices[category]);
    }
  }

  return payload;
}

export function encodeConsent(choices: ConsentChoices, now: Date): string {
  const record: ConsentRecord = {
    v: CONSENT_VERSION,
    ts: now.toISOString(),
    categories: {
      analytics: !!choices.analytics,
      marketing: !!choices.marketing,
      preferences: !!choices.preferences,
    },
  };
  return JSON.stringify(record);
}

/**
 * Read a stored record back, or `null` for anything we should re-ask about.
 *
 * Anything unparseable, any version that is not the current one, and any
 * missing category all fold to `null`. The failure mode that matters is the
 * silent one — a half-read record that grants analytics because a boolean was
 * `undefined` and `undefined` is not `false` in the eyes of a truthiness check.
 */
export function decodeConsent(raw: string | null | undefined): ConsentRecord | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;

  const record = parsed as Partial<ConsentRecord>;
  if (record.v !== CONSENT_VERSION) return null;
  if (typeof record.ts !== 'string') return null;

  const categories = record.categories;
  if (typeof categories !== 'object' || categories === null) return null;

  const choices = {} as ConsentChoices;
  for (const category of CONSENT_CATEGORIES) {
    const value = (categories as Record<string, unknown>)[category];
    if (typeof value !== 'boolean') return null;
    choices[category] = value;
  }

  return { v: record.v, ts: record.ts, categories: choices };
}

/**
 * The domain to scope the cookie to, so `proxyhive.io` and `app.proxyhive.io`
 * share one answer.
 *
 * Last two labels. That is correct for every host this estate serves — four
 * `.io` domains and their `app.`/`www.` subdomains — and wrong for a
 * multi-label suffix like `.co.uk`, which we do not own and which would need a
 * public-suffix list to get right. Returning `null` for anything without a dot
 * (a bare container name), for a literal IP, or for a development host
 * ({@link isDevelopmentHost}) leaves the attribute off, which scopes the cookie
 * to the exact host: the right answer where there is no parent domain this
 * estate owns to share with.
 */
export function cookieDomainFor(hostname: string): string | null {
  if (!hostname || /^[\d.]+$/.test(hostname) || hostname.includes(':')) return null;
  if (isDevelopmentHost(hostname)) return null;

  const labels = hostname.split('.');
  if (labels.length < 2) return null;

  return `.${labels.slice(-2).join('.')}`;
}

/**
 * Is this the estate running on somebody's machine rather than a real surface?
 *
 * Two suffixes: `.localhost`, and `.traefik.me` — public wildcard DNS answering
 * 127.0.0.1 at any depth, which is what local development is served on, so a
 * developer's dashboard is `app.proxyforge.traefik.me`.
 *
 * Both callers below need this and for different reasons, and neither is
 * cosmetic:
 *
 *  - {@link cookieDomainFor} would otherwise take the last two labels and scope
 *    the consent cookie to `.traefik.me` — a registrable domain shared with
 *    every other user of that service, and not one this estate owns.
 *  - {@link shouldLoadTag} would otherwise treat a development host as real and
 *    post local pageviews into the production property, where they are
 *    indistinguishable from customers' and quietly wrong for as long as nobody
 *    looks.
 *
 * Matched on a label boundary rather than as a bare substring, so `nottraefik.me`
 * and `traefik.me.example.com` stay real hostnames.
 */
function isDevelopmentHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname.endsWith('.localhost') ||
    hostname === 'traefik.me' ||
    hostname.endsWith('.traefik.me')
  );
}

/**
 * Should a tag load here at all?
 *
 * Two independent reasons not to, and they are checked in this order because
 * the second is about *whose* data it would be: a container id has to be
 * configured, and the hostname has to be a real one. See
 * {@link isDevelopmentHost} for which hostnames are not, and why letting one
 * through is expensive.
 */
export function shouldLoadTag(containerId: string | undefined, hostname: string): boolean {
  if (!containerId) return false;
  if (!hostname) return false;
  if (isDevelopmentHost(hostname)) return false;
  if (hostname === '127.0.0.1' || hostname === '::1') return false;
  return true;
}

/**
 * Countries where a visitor must be *asked* before anything optional is set,
 * rather than tracked until they object.
 *
 * The EEA thirty (the EU twenty-seven plus Iceland, Liechtenstein and Norway),
 * the United Kingdom, and Switzerland. The UK left the EU and kept the rule;
 * Switzerland was never in it and its revised FADP asks for the same thing in
 * different words. Grouping the three is a policy decision, not a legal
 * finding: it is the smallest set that lets one banner satisfy all of them.
 *
 * **Everywhere else is tracked by default.** That is the commercial choice this
 * estate has made, and it is the reason this list exists as data rather than as
 * a boolean — the moment a jurisdiction is added it is one entry here plus one
 * container-side region, not a rewrite of the banner.
 *
 * It is deliberately *not* a list of every privacy law in the world. California
 * wants a "Do Not Sell or Share" link rather than a prior ask, and Brazil's
 * LGPD wants a lawful basis that need not be consent. Neither is served by
 * putting them in this array, and putting them here would claim a compliance
 * this code does not implement.
 */
export const CONSENT_REQUIRED_REGIONS: readonly string[] = [
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA, beyond the EU
  'IS', 'LI', 'NO',
  // Kept the rule after leaving, and never had it but arrived at the same place
  'GB', 'CH',
] as const;

/**
 * IANA time zones belonging to the countries above.
 *
 * **Why a time zone and not an IP lookup.** The banner has to decide before
 * anything is stored, in a bundle that is served from a CDN edge to four
 * hostnames, and two of the four marketing sites are static exports with no
 * server to ask. `Intl.DateTimeFormat().resolvedOptions().timeZone` is present
 * in every browser this estate supports, costs no round trip, and cannot fail
 * open the way a blocked geolocation request can.
 *
 * **This map is not the compliance boundary and must not be read as one.** The
 * Consent Mode defaults set in the page carry `region: CONSENT_REQUIRED_REGIONS`,
 * and Google resolves *that* from the visitor's IP at its own end. So a visitor
 * in Berlin whose laptop clock says `America/Denver` still has their tags
 * default to denied; all this map decides is whether they are shown a banner.
 * The failure mode is therefore a banner shown to someone who did not need one,
 * or not shown to someone whose tags are denied anyway — never a tag that fires
 * without a legal basis.
 *
 * Only zones for the countries in {@link CONSENT_REQUIRED_REGIONS} appear. A
 * zone that is absent is not an error; it means "not a region that must be
 * asked", which is the answer for most of the world.
 *
 * Three edge cases are decided here rather than left to whoever reads this next:
 *
 *  - **France's outermost regions are in** (Guadeloupe, Martinique, Guyane,
 *    Réunion, Mayotte) and its overseas countries and territories are out
 *    (New Caledonia, French Polynesia, Saint-Pierre-et-Miquelon). The former
 *    are EU territory where the GDPR applies directly; the latter are not.
 *  - **Denmark's north Atlantic is out.** The Faroes and Greenland are
 *    constitutionally Danish and outside the EU, with their own data law.
 *  - **The Crown Dependencies and Gibraltar are out.** Jersey, Guernsey, the
 *    Isle of Man and Gibraltar are not the United Kingdom and each legislates
 *    its own equivalent. They are close enough to `GB` to be assumed in by
 *    accident, which is why they are named here as excluded.
 */
const REGION_TIME_ZONES: Readonly<Record<string, string>> = {
  'Europe/Vienna': 'AT',
  'Europe/Brussels': 'BE',
  'Europe/Sofia': 'BG',
  'Europe/Zagreb': 'HR',
  'Asia/Nicosia': 'CY',
  'Asia/Famagusta': 'CY',
  'Europe/Nicosia': 'CY',
  'Europe/Prague': 'CZ',
  'Europe/Copenhagen': 'DK',
  'Europe/Tallinn': 'EE',
  'Europe/Helsinki': 'FI',
  'Europe/Mariehamn': 'FI',
  'Europe/Paris': 'FR',
  'America/Guadeloupe': 'FR',
  'America/Martinique': 'FR',
  'America/Cayenne': 'FR',
  'Indian/Reunion': 'FR',
  'Indian/Mayotte': 'FR',
  'Europe/Berlin': 'DE',
  'Europe/Busingen': 'DE',
  'Europe/Athens': 'GR',
  'Europe/Budapest': 'HU',
  'Europe/Dublin': 'IE',
  'Europe/Rome': 'IT',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  'Europe/Luxembourg': 'LU',
  'Europe/Malta': 'MT',
  'Europe/Amsterdam': 'NL',
  'Europe/Warsaw': 'PL',
  'Europe/Lisbon': 'PT',
  'Atlantic/Madeira': 'PT',
  'Atlantic/Azores': 'PT',
  'Europe/Bucharest': 'RO',
  'Europe/Bratislava': 'SK',
  'Europe/Ljubljana': 'SI',
  'Europe/Madrid': 'ES',
  'Africa/Ceuta': 'ES',
  'Atlantic/Canary': 'ES',
  'Europe/Stockholm': 'SE',
  'Atlantic/Reykjavik': 'IS',
  'Europe/Vaduz': 'LI',
  'Europe/Oslo': 'NO',
  'Arctic/Longyearbyen': 'NO',
  'Europe/London': 'GB',
  'Europe/Zurich': 'CH',
};

/**
 * The visitor's country, as far as a time zone can state it, or `null`.
 *
 * `null` means two different things on purpose — "the browser would not tell
 * us" and "the zone is not one that must be asked" — because the only caller
 * treats them the same way and collapsing them here keeps the two-value answer
 * from leaking into every call site. {@link consentRegionRequired} is where the
 * distinction would matter, and it does not.
 */
export function regionFromTimeZone(timeZone: string | null | undefined): string | null {
  if (!timeZone) return null;
  return REGION_TIME_ZONES[timeZone] ?? null;
}

/** The active time zone, or `null` where `Intl` is unavailable or throws. */
export function currentTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Must this visitor be asked before anything optional is stored?
 *
 * **Unknown means ask.** A browser that reports no time zone at all — `Intl`
 * missing, a hardened privacy build, a test environment — is treated as a
 * region that requires consent. That is the direction where a mistake costs a
 * banner rather than a breach, and it is the reason this returns `true` for
 * `null` rather than falling through to the tracked-by-default branch.
 *
 * A recognised zone outside {@link CONSENT_REQUIRED_REGIONS} is *not* unknown:
 * `America/Denver` resolves to no entry and correctly answers `false`.
 */
export function consentRegionRequired(timeZone: string | null | undefined): boolean {
  if (!timeZone) return true;
  const region = regionFromTimeZone(timeZone);
  if (region === null) return false;
  return CONSENT_REQUIRED_REGIONS.includes(region);
}

/**
 * What this visitor's consent actually is right now.
 *
 * The stored answer if they gave one. Otherwise the default their region gets:
 * denied where they must be asked, granted where they are tracked until they
 * object.
 *
 * **This is what the preferences panel must render**, and getting it wrong is
 * subtle rather than loud. Falling back to {@link CONSENT_DENIED} for everyone
 * would show a visitor in Denver three unticked boxes while their pageviews
 * were being counted under a granted default — a panel that lies about the
 * state it exists to display, and the one screen where that is least excusable.
 */
export function effectiveChoices(
  record: ConsentRecord | null,
  timeZone: string | null | undefined,
): ConsentChoices {
  if (record) return record.categories;
  return consentRegionRequired(timeZone) ? CONSENT_DENIED : CONSENT_GRANTED;
}

/**
 * Should the banner be shown at all?
 *
 * Only when there is no stored answer *and* the visitor is somewhere that must
 * be asked. Everywhere else the banner never renders — which is the point of
 * the region split — and the footer's "Cookie settings" remains the way to
 * change an answer that was never explicitly given.
 */
export function shouldAskConsent(
  record: ConsentRecord | null,
  timeZone: string | null | undefined,
): boolean {
  if (record) return false;
  return consentRegionRequired(timeZone);
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    /**
     * Defined by the inline default-consent script, not by gtm.js. It pushes
     * `arguments` onto `dataLayer`, so calling it before the container has
     * loaded is not merely safe — it is the intended order.
     */
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Tell Google the visitor has answered.
 *
 * Safe to call before gtm.js has loaded: `gtag` is a `dataLayer.push` shim
 * installed by the inline default script, and the container replays the queue
 * when it arrives. The extra `dataLayer` event is what container-side triggers
 * key off — Consent Mode signals are not events and cannot fire a tag on their
 * own, so without it a tag configured to wait for consent waits forever.
 */
export function pushConsentUpdate(choices: ConsentChoices): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer ?? [];

  // Absent only when no container is configured, in which case the inline
  // default script never rendered and there is nothing listening. The event
  // below is still pushed: `dataLayer` is a plain array and a queue nobody
  // drains costs nothing, while a missing entry is a gap in the record.
  window.gtag?.('consent', 'update', consentModePayload(choices));
  window.gtag?.('set', 'ads_data_redaction', !choices.marketing);

  window.dataLayer.push({ event: 'consent_update', ...choices });
}

/** Browser-side cookie read. Returns `null` off the browser. */
export function readConsentCookie(): ConsentRecord | null {
  if (typeof document === 'undefined') return null;

  const prefix = `${CONSENT_COOKIE}=`;
  const hit = document.cookie
    .split('; ')
    .find((pair) => pair.startsWith(prefix));

  if (!hit) return null;

  try {
    return decodeConsent(decodeURIComponent(hit.slice(prefix.length)));
  } catch {
    return null;
  }
}

/**
 * Browser-side cookie write.
 *
 * `SameSite=Lax` rather than `Strict`: a visitor arriving from a search result
 * or an ad must not be asked again because the navigation was cross-site.
 * `Secure` is conditional on the page already being https, or the cookie would
 * be silently dropped on a plain-http development host.
 */
export function writeConsentCookie(choices: ConsentChoices, now: Date = new Date()): void {
  if (typeof document === 'undefined') return;

  const domain = cookieDomainFor(window.location.hostname);
  const parts = [
    `${CONSENT_COOKIE}=${encodeURIComponent(encodeConsent(choices, now))}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  if (domain) parts.push(`Domain=${domain}`);
  if (window.location.protocol === 'https:') parts.push('Secure');

  document.cookie = parts.join('; ');
}
